/**
 * Intent Gap Analysis Service
 * 
 * Backend: Core API (seo-engine-core-api)
 * Endpoint: GET /api/v1/sites/{siteId}/intent-gaps
 * 
 * Purpose: Fetch pre-computed intent gap opportunities from earlystage.intent_gap_opportunity table
 * ETL Source: earlystage-batch service (IntentGapAnalysisETLService)
 * Data: Based on GSC Performance data (last 90 days)
 */

import { api } from './api';

export interface IntentGapOpportunity {
  id: number;
  tenantId: number;
  siteId: number;
  url: string;
  query: string;
  impressions: number;
  clicks: number;
  ctr: number;
  position: number;
  intentType: string;
  priorityScore: number;
  suggestion: string;
  analyzedDateRangeStart: string;
  analyzedDateRangeEnd: string;
  createdAt: string;
  updatedAt: string;
}

export interface IntentGapFilters {
  intentType?: string;
  limit?: number;
}

/**
 * Get intent gap opportunities for a site
 */
export const getIntentGapOpportunities = async (
  siteId: number,
  filters?: IntentGapFilters
): Promise<IntentGapOpportunity[]> => {
  const params = new URLSearchParams();
  
  if (filters?.intentType) {
    params.append('intentType', filters.intentType);
  }
  
  if (filters?.limit) {
    params.append('limit', filters.limit.toString());
  }

  const queryString = params.toString();
  const url = `/earlystage/sites/${siteId}/intent-gaps${queryString ? `?${queryString}` : ''}`;
  
  const response = await api.get<IntentGapOpportunity[]>(url);
  return response.data;
};

/**
 * Get intent gap opportunities for a specific URL
 */
export const getIntentGapOpportunitiesByUrl = async (
  siteId: number,
  url: string
): Promise<IntentGapOpportunity[]> => {
  const response = await api.get<IntentGapOpportunity[]>(
    `/earlystage/sites/${siteId}/intent-gaps/url`,
    {
      params: { url },
    }
  );
  return response.data;
};

export const intentGapService = {
  getIntentGapOpportunities,
  getIntentGapOpportunitiesByUrl,
};
